import { useState } from 'react'
import { Album } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import { createEmptyAlbum, renameAlbum, deleteEmptyAlbum, changeDateAlbum } from '../lib/folder-manager'
import { folderLogger } from '../lib/logger'
import type { Feedback } from './use-feedback'

export interface DeleteAlbumTarget {
  uuid: string
  name: string
  count: number
}

// The server refuses a delete whose asserted file count disagrees with its
// own (SOL-174). Its message states the fact; the user needs the instruction.
function deleteErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'FILE_COUNT_MISMATCH') {
    return 'This album changed since you opened it — reopen it and try again.'
  }
  if (error instanceof ApiError && error.code === 'FILE_COUNT_UNAVAILABLE') {
    return 'This album could not be read just now — try again in a moment.'
  }
  return error instanceof Error ? error.message : 'Failed to delete album'
}

export interface AlbumMutations {
  handleCreateFolder: () => void
  handleRenameAlbum: (albumId: string, newName: string) => void
  handleChangeDateAlbum: (albumId: string, newDate: string) => void
  handleDeleteAlbum: (albumId: string) => void
  handleDeleteAlbumConfirm: () => Promise<void>
  deleteModal: {
    isOpen: boolean
    target: DeleteAlbumTarget | null
    confirmText: string
    loading: boolean
    setConfirmText: (text: string) => void
    close: () => void
  }
}

// Create, rename, change-date, and BOTH delete paths (SOL-150 Task 5). The
// empty-album and typed-name delete paths look like two features but share
// the sub-album guard and the SOL-174 file-count assertion below, so
// splitting them by which modal they render would cut across that.
export function useAlbumMutations(args: {
  albums: Album[]
  updateAlbums: (albums: Album[]) => void
  fetchAlbums: (silent?: boolean) => Promise<void>
  currentFolderId: string | undefined
  currentUserId: string | undefined
  /** The Memoir new albums are created in; undefined where creating is not allowed. */
  memoirId?: string
  feedback: Feedback
}): AlbumMutations {
  const { albums, updateAlbums, fetchAlbums, currentFolderId, currentUserId, memoirId, feedback } = args
  const { showNotification, showConfirm, closeConfirm, showToast } = feedback

  const handleCreateFolder = () => {
    if (!currentUserId) return

    const result = createEmptyAlbum(albums, 'New Album', currentFolderId, currentUserId, memoirId)
    if (result.success && result.albums) {
      updateAlbums(result.albums)
    } else {
      showNotification('error', 'Error', result.error || 'Failed to create album')
    }
  }

  const handleRenameAlbum = (albumId: string, newName: string) => {
    const result = renameAlbum(albums, albumId, newName)
    if (result.success && result.albums) {
      updateAlbums(result.albums)
    } else {
      showNotification('error', 'Error', result.error || 'Failed to rename album')
    }
  }

  // Delete-album confirmation (non-empty albums require typing the album name)
  const [deleteAlbumModalOpen, setDeleteAlbumModalOpen] = useState(false)
  const [deleteAlbumTarget, setDeleteAlbumTarget] = useState<DeleteAlbumTarget | null>(null)
  const [deleteAlbumConfirmText, setDeleteAlbumConfirmText] = useState('')
  const [deleteAlbumLoading, setDeleteAlbumLoading] = useState(false)

  const handleDeleteAlbumConfirm = async () => {
    if (!deleteAlbumTarget || deleteAlbumLoading) return
    if (deleteAlbumConfirmText !== deleteAlbumTarget.name) return

    setDeleteAlbumLoading(true)
    try {
      await apiClient.deleteAlbum(deleteAlbumTarget.uuid, deleteAlbumTarget.count)
      updateAlbums(albums.filter(a => a.uuid !== deleteAlbumTarget.uuid))
      showToast(`Album "${deleteAlbumTarget.name}" deleted`)
      setDeleteAlbumModalOpen(false)
      setDeleteAlbumTarget(null)
      fetchAlbums(true)
    } catch (error) {
      folderLogger.error('Failed to delete album', { error })
      showNotification('error', 'Delete Failed', deleteErrorMessage(error))
      // Close the modal and clear its stale snapshot — deleteAlbumTarget.count
      // is fixed at the moment the modal opened, so leaving it open would let
      // the user retry with the identical, already-refused count forever.
      setDeleteAlbumModalOpen(false)
      setDeleteAlbumTarget(null)
      setDeleteAlbumConfirmText('')
      // Pull a fresh listing so the user's next attempt is made against what
      // the server actually holds rather than the snapshot that was refused.
      fetchAlbums(true)
    } finally {
      setDeleteAlbumLoading(false)
    }
  }

  const handleDeleteAlbum = (albumId: string) => {
    const album = albums.find(a => a.uuid === albumId)
    if (!album) return

    if (album.isEmpty) {
      // Empty album - can delete
      showConfirm(
        'Delete Album',
        `Are you sure you want to delete "${album.name}"?`,
        async () => {
          const isTempAlbum = albumId.startsWith('temp_')

          if (isTempAlbum) {
            // Local-only temp album - just delete locally
            const result = deleteEmptyAlbum(albums, albumId)
            if (result.success && result.albums) {
              updateAlbums(result.albums)
              closeConfirm()
            } else {
              closeConfirm()
              showNotification('error', 'Error', result.error || 'Failed to delete album')
            }
          } else {
            // Real backend album - call API to delete
            try {
              // Zero, not omitted. This branch exists BECAUSE the client thinks
              // the album is empty, and that belief is the thing the server is
              // being asked to confirm before it cascades over the photos.
              await apiClient.deleteAlbum(albumId, 0)

              // Remove from local state
              const result = deleteEmptyAlbum(albums, albumId)
              if (result.success && result.albums) {
                updateAlbums(result.albums)
              }

              closeConfirm()
              showToast(`Album "${album.name}" deleted`)
            } catch (error) {
              closeConfirm()
              folderLogger.error('Failed to delete album', { error })
              showNotification('error', 'Delete Failed', deleteErrorMessage(error))
              fetchAlbums(true)
            }
          }
        },
        { variant: 'danger', confirmText: 'Delete' }
      )
    } else {
      // Non-empty album: the backend cascade deletes the album's own photos
      // but not sub-albums, so block if this album has children; otherwise
      // require a typed-name confirmation before the destructive delete.
      const hasChildren = albums.some(a => a.parentId === albumId)
      if (hasChildren) {
        showNotification(
          'info',
          'Delete sub-albums first',
          `"${album.name}" contains sub-albums. Delete or move them before deleting this album.`
        )
        return
      }
      setDeleteAlbumTarget({ uuid: albumId, name: album.name, count: album.files?.length ?? 0 })
      setDeleteAlbumConfirmText('')
      setDeleteAlbumModalOpen(true)
    }
  }

  const handleChangeDateAlbum = (albumId: string, newDate: string) => {
    const result = changeDateAlbum(albums, albumId, newDate)
    if (result.success && result.albums) {
      updateAlbums(result.albums)
    } else {
      showNotification('error', 'Error', result.error || 'Failed to change album date')
    }
  }

  return {
    handleCreateFolder,
    handleRenameAlbum,
    handleChangeDateAlbum,
    handleDeleteAlbum,
    handleDeleteAlbumConfirm,
    deleteModal: {
      isOpen: deleteAlbumModalOpen,
      target: deleteAlbumTarget,
      confirmText: deleteAlbumConfirmText,
      loading: deleteAlbumLoading,
      setConfirmText: setDeleteAlbumConfirmText,
      close: () => setDeleteAlbumModalOpen(false),
    },
  }
}
